import React, { useMemo } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import OfficeBuildingIcon from '@atlaskit/icon/core/office-building';
import { Text } from '@atlaskit/primitives/compiled';

import type { SelectFilterOption } from '../../../../../common/constants/filters/select-filter/types';
import { MAX_SELECTED_SITES } from '../../../../../common/constants/filters/site';
import type { FilterProps } from '../../../../../common/ui/filters/product-filters/types';
import SelectFilter from '../../../../../common/ui/filters/select-filter';
import { createOptionLabelFormatter } from '../../../../../common/ui/filters/select-filter/option-label';
import { useSelectFilter } from '../../../../../controllers/filters/base-select-filter';
import {
	useCloudId,
	useFiltersDiscoverability2Enabled,
} from '../../../../../controllers/store/bootstrap';
import { siteFilterId } from '../../../../../controllers/store/search/filters/filter-ids';

import { i18n } from './messages';
import { SiteAvatar } from './site-avatar';

// TODO: source orgName/unitName from the combined config. Both are optional — when neither is
// available we render a flat list with no section headings.
const useCollaborationContextSites = (): {
	orgName?: string;
	unitName?: string;
	primarySiteName?: string;
} => {
	// return { orgName: 'Atlassian' };
	return { primarySiteName: 'hello' };
};

const renderHint = (chunks: React.ReactNode[]): React.ReactNode => (
	<Text as="em" weight="regular" size="small" color="inherit">
		{chunks}
	</Text>
);

const SiteSelectFilter = ({
	isFilterInDropdown,
	fit = 'default',
	onFilterChange,
	hideOnHoverOut,
	defaultIsOpen,
}: FilterProps & { defaultIsOpen?: boolean }): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const [primarySiteId] = useCloudId();
	const isFiltersDiscoverability2Enabled = useFiltersDiscoverability2Enabled();
	const { orgName, unitName, primarySiteName } = useCollaborationContextSites();
	const [filterData, { loadInitialOptions, setOption: setOptionForSelectFilter }] = useSelectFilter(
		{
			filterId: siteFilterId,
		},
	);

	const formatOptionLabel = useMemo(
		() =>
			createOptionLabelFormatter(({ option }) => (
				<SiteAvatar
					displayName={option.label ?? ''}
					// Each option carries its own `isSelected` (the current site is always true), so we don't
					// need to close over the selected list here.
					isActive={Boolean((option as { isSelected?: boolean }).isSelected)}
				/>
			)),
		[],
	);

	if (!filterData) {
		return null;
	}

	const { selectedOptions, options, isLoading } = filterData;

	// The current/primary site is always selected and shown first. It is injected here for display
	// only — never a stored option of the `site` filter, so it is never serialized to `?sites=`.
	// `isDisabled` locks it.
	const currentSiteOption: (SelectFilterOption & { isDisabled: boolean }) | undefined =
		primarySiteId
			? {
					value: primarySiteId,
					trackingKey: primarySiteId,
					queryParamValue: primarySiteId,
					// TODO: use the current site's real name from the combined config. `orgName` is the org,
					// not the site, so fall back to the cloudId rather than rendering a blank "(current)".
					label: formatMessage(i18n.currentSiteLabel, { name: primarySiteName }),
					isSelected: true,
					isSuggested: false,
					isDefault: false,
					isDisabled: true,
				}
			: undefined;

	// Stable partition of the non-current sites: selected first, then the rest, preserving each group's
	// relative order (two filters => stable) so selecting/deselecting moves an option up/down without
	// reordering the others. The current/primary site stays pinned at the very top.
	const nonCurrentOptions = options.filter((option) => option.value !== primarySiteId);
	const orderedNonCurrentOptions = [
		...nonCurrentOptions.filter((option) => option.isSelected),
		...nonCurrentOptions.filter((option) => !option.isSelected),
	];
	const optionsWithCurrentSite = currentSiteOption
		? [currentSiteOption, ...orderedNonCurrentOptions]
		: orderedNonCurrentOptions;
	const selectedOptionsWithCurrentSite = currentSiteOption
		? [currentSiteOption, ...selectedOptions.filter((option) => option.value !== primarySiteId)]
		: selectedOptions;

	// NOTE: When unitName or orgName is not available, we do not display section headings
	const availableSectionTitle = unitName ?? orgName;
	const showSectionHeadings = Boolean(availableSectionTitle);

	const availableGroupOptions = options.filter(
		(option) => option.value !== primarySiteId && !option.isSelected,
	);
	const groupedOptions = [
		{ label: formatMessage(i18n.selectedHeadingPlain), options: selectedOptionsWithCurrentSite },
		{ label: availableSectionTitle ?? '', options: availableGroupOptions },
	].filter((group) => group.options.length > 0);

	const selectedSectionHeading: React.ReactNode =
		selectedOptionsWithCurrentSite.length <= 1 ? (
			<FormattedMessage {...i18n.selectedHeadingRequired} values={{ hint: renderHint }} />
		) : optionsWithCurrentSite.length >= MAX_SELECTED_SITES ? (
			<FormattedMessage
				{...i18n.selectedHeadingMax}
				values={{ max: MAX_SELECTED_SITES, hint: renderHint }}
			/>
		) : (
			<FormattedMessage {...i18n.selectedHeadingPlain} />
		);

	return (
		<SelectFilter
			contentFit={fit}
			checkboxIndicators
			defaultIsOpen={defaultIsOpen}
			onDropdownOpen={loadInitialOptions}
			onSelectChange={(option) => {
				const changed = Array.isArray(option) ? option[0] : option;
				// Never route the current/primary site into the filter — it is display-only and must stay selected
				if (!changed || changed.value === primarySiteId) {
					return;
				}
				setOptionForSelectFilter(changed);
				onFilterChange?.(changed);
			}}
			options={showSectionHeadings ? groupedOptions : optionsWithCurrentSite}
			selectedOptions={selectedOptionsWithCurrentSite}
			label={primarySiteName || formatMessage(i18n.label)}
			filterOrControlId={'site'}
			isLoading={isLoading}
			formatOptionLabel={formatOptionLabel}
			isFilterInDropdown={isFilterInDropdown}
			isFiltersDiscoverability2Enabled={isFiltersDiscoverability2Enabled}
			hideOnHoverOut={hideOnHoverOut}
			iconBefore={<OfficeBuildingIcon label="" />}
			// Only show section headings when there is a unit or org name
			formatGroupLabel={
				showSectionHeadings
					? (group) =>
							group.options.some((option) => option.value === primarySiteId)
								? selectedSectionHeading
								: group.label
					: undefined
			}
			// `selectedOptions` keeps every selected site so the +N badge counts additional sites (e.g.
			// "+1" when one other site is selected), but the chip stays greyed until the user picks a
			// site beyond the always-required current one.
			hasNeutralBackground={selectedOptionsWithCurrentSite.length <= 1}
			isSelectedOverride={selectedOptionsWithCurrentSite.length > 1}
			getFirstSelectedLabel={() => ''}
		/>
	);
};

export default SiteSelectFilter;
